/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* Licensed under the MIT License. See LICENSE.md in the project root for license terms.
*--------------------------------------------------------------------------------------------*/

import { useCallback, useEffect, useState } from "react";
import { Annotation } from "../../types/Annotation";
import { SvgCaretLeft, SvgCaretRight, SvgNew } from "@itwin/itwinui-icons-react";
import { Button, Tooltip } from "@itwin/itwinui-react";
import { DoublyLinkedNode } from "./linked-list/DoublyLinkedNode";
import styles from "./WalkthroughFooter.module.css";

export interface WalkthroughFooterProps {
  step?: DoublyLinkedNode<Annotation>;
  onStepChange: (nextStep: DoublyLinkedNode<Annotation>) => void;
  onEmphasisClick?: () => void;
}

export const WalkthroughFooter = ({ step, onStepChange, onEmphasisClick }: WalkthroughFooterProps) => {
  const [next, setNext] = useState<DoublyLinkedNode<Annotation>>();
  const [prev, setPrev] = useState<DoublyLinkedNode<Annotation>>();

  useEffect(() => {
    if (step) {
      setNext(() => {
        let next = step.next;
        while (next !== undefined && !step?.value.skip && next.value.skip) {
          next = next?.next;
        }
        return next;
      });

      setPrev(() => {
        let prev = step.prev;
        while (prev !== undefined && !step?.value.skip && prev.value.skip) {
          prev = prev?.prev;
        }
        return prev;
      });
    }
  }, [step])

  const onProceedClick = useCallback(() => {
    if (!next)
      return;
    onStepChange(next)
  }, [onStepChange, next]);

  const onBackClick = useCallback(() => {
    if (!prev)
      return;
    onStepChange(prev);
  }, [onStepChange, prev])

  return (
    <div className={styles.footer}>
      <Button endIcon={<SvgCaretLeft />} styleType="borderless" onClick={onBackClick} size="small" className={[styles.buttonBack, !prev ? styles.hidden : ""].join(" ")}>
        {prev?.value.title}
      </Button>
      <Tooltip placement="top" delay={500} content={"Bring related file section to focus in the editor"}>
        <Button startIcon={<SvgNew />} onClick={onEmphasisClick} size="small" />
      </Tooltip>
      {next && <Button startIcon={<SvgCaretRight />} styleType="borderless" onClick={onProceedClick} size="small" className={[styles.buttonForward, !next ? styles.hidden : ""].join(" ")}>
        {next?.value.title}
      </Button>}
    </div>);
}
